import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Avatar, Badge, Card, CopyValue } from '@wit-support/ui';
import { IdentityChip } from './identity-chip.js';
import { cn } from './lib/cn.js';
import { computeInitials } from './lib/initials.js';
const ContactCardContext = React.createContext(null);
function useContactCardContext(component) {
    const ctx = React.useContext(ContactCardContext);
    if (!ctx) {
        throw new Error(`<${component}> must be rendered inside <ContactCard>`);
    }
    return ctx;
}
const ContactCardRoot = React.forwardRef(({ contact, children, className, ...rest }, ref) => {
    const value = React.useMemo(() => ({ contact }), [contact]);
    return (_jsx(ContactCardContext.Provider, { value: value, children: _jsx(Card, { ref: ref, className: cn('overflow-hidden', className), ...rest, children: children }) }));
});
ContactCardRoot.displayName = 'ContactCard';
const ContactCardHeader = React.forwardRef(({ className, ...rest }, ref) => {
    const { contact } = useContactCardContext('ContactCard.Header');
    const initials = computeInitials(contact.displayName, contact.primaryEmail);
    const displayName = contact.displayName?.trim();
    const primaryEmail = contact.primaryEmail;
    const nameSlot = displayName && displayName.length > 0 ? displayName : primaryEmail;
    const emailSlot = displayName && primaryEmail ? primaryEmail : null;
    return (_jsxs("div", { ref: ref, className: cn('flex items-center gap-3 p-4', className), ...rest, children: [_jsx(Avatar, { className: "h-10 w-10 text-sm", children: _jsx(Avatar.Fallback, { className: "font-medium bg-[var(--color-surface-sunken)] text-[var(--color-text-secondary)]", children: initials }) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "truncate text-base font-semibold text-[var(--color-text-primary)]", title: nameSlot ?? undefined, children: nameSlot ?? 'Unknown contact' }), emailSlot ? (
                    // Emails are primary identifiers — never hide them behind an ellipsis
                    // in a narrow rail. `break-all` wraps at any char so the full value
                    // stays visible; `title` gives a full-text tooltip on hover.
                    _jsx("p", { className: "text-sm text-[var(--color-text-muted)] break-all", title: emailSlot, children: emailSlot })) : null] })] }));
});
ContactCardHeader.displayName = 'ContactCard.Header';
const ContactCardIdentities = React.forwardRef(({ className, ...rest }, ref) => {
    const { contact } = useContactCardContext('ContactCard.Identities');
    const identities = contact.identities ?? [];
    return (_jsx("div", { ref: ref, className: cn('border-t border-[var(--color-border-subtle)] p-4', className), ...rest, children: identities.length === 0 ? (_jsx("p", { className: "text-sm text-[var(--color-text-muted)]", children: "\u0414\u0440\u0443\u0433\u0438\u0445 \u0438\u0434\u0435\u043D\u0442\u0438\u0447\u043D\u043E\u0441\u0442\u0435\u0439 \u043D\u0435\u0442" })) : (_jsx("ul", { className: "flex flex-wrap gap-2", children: identities.map((identity, i) => (_jsx("li", { children: _jsx(IdentityChip, { identity: identity }) }, `${identity.kind}:${identity.value}:${i}`))) })) }));
});
ContactCardIdentities.displayName = 'ContactCard.Identities';
/** Russian label for a raw gender token (best-effort; passes through unknowns). */
function genderLabelRu(gender) {
    const g = gender.trim().toLowerCase();
    if (g === 'male' || g === 'm' || g === 'м' || g === 'мужской')
        return 'Мужской';
    if (g === 'female' || g === 'f' || g === 'ж' || g === 'женский')
        return 'Женский';
    if (g === 'other' || g === 'другое')
        return 'Другое';
    return gender;
}
/**
 * Строка блока идентификации: подпись + значение. Идентификаторы (WIT user,
 * Stripe) кладутся сюда как `CopyValue` кита — вся строка значения копируется
 * по клику, иконка проявляется по наведению, тост «Скопировано». Отдельной
 * кнопки рядом нет: идентификатор длинный и моноширинный, кнопка только
 * съедала бы ширину рейла. `dd` тянется на всю оставшуюся ширину (`flex-1`):
 * без этого столбец сжимался до max-content кнопки, и отрицательные поля
 * `CopyValue` (`-mx-1`) на округлении отнимали у текста последний символ.
 */
function IdentityRow({ label, children, mono = false, }) {
    return (_jsxs("div", { className: "flex items-baseline gap-2", children: [_jsxs("dt", { className: "shrink-0 text-[var(--color-text-muted)]", children: [label, ":"] }), _jsx("dd", { className: cn('flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1 text-[var(--color-text-primary)]', mono && 'font-mono'), children: children })] }));
}
/**
 * Identity slot: surfaces the resolved WIT user id, the Stripe customer
 * id(s) and gender/age. Identifiers come first (WIT user, Stripe — both
 * click-to-copy), attributes second (Пол, Возраст). Renders the
 * "найден в WIT" badge whenever the contact resolved to a WIT user; otherwise
 * shows nothing for that field. Returns `null` entirely when no identity facts
 * are present, so the slot collapses cleanly for unidentified contacts.
 */
const ContactCardIdentity = React.forwardRef(({ className, ...rest }, ref) => {
    const { contact } = useContactCardContext('ContactCard.Identity');
    const witUserId = contact.witUserId ?? null;
    const stripeCustomerIds = contact.stripeCustomerIds ?? [];
    const gender = contact.gender ?? null;
    const age = contact.age ?? null;
    // Nothing to show — collapse the slot.
    if (!witUserId && stripeCustomerIds.length === 0 && !gender && age === null)
        return null;
    return (_jsxs("div", { ref: ref, className: cn('flex flex-col gap-2 border-t border-[var(--color-border-subtle)] p-4', className), ...rest, children: [witUserId ? (_jsx("div", { className: "flex items-center gap-2", children: _jsx(Badge, { variant: "success", size: "sm", children: "\u043D\u0430\u0439\u0434\u0435\u043D \u0432 WIT" }) })) : null, _jsxs("dl", { className: "flex flex-col gap-1 text-sm", children: [witUserId ? (_jsx(IdentityRow, { label: "WIT user", mono: true, children: _jsx(CopyValue, { value: witUserId, label: `WIT user id ${witUserId}` }) })) : null, stripeCustomerIds.length > 0 ? (
                    // Несколько id — через запятую в ТОЙ ЖЕ строке, каждый копируется
                    // отдельно: у контакта с двумя клиентами Stripe оператору нужен
                    // конкретный, а не оба через пробел.
                    _jsx(IdentityRow, { label: "Stripe", mono: true, children: stripeCustomerIds.map((id, i) => (_jsxs(React.Fragment, { children: [i > 0 ? (_jsx("span", { "aria-hidden": "true", className: "text-[var(--color-text-muted)]", children: "," })) : null, _jsx(CopyValue, { value: id, label: `Stripe customer id ${id}` })] }, id))) })) : null, gender ? _jsx(IdentityRow, { label: "\u041F\u043E\u043B", children: genderLabelRu(gender) }) : null, age !== null ? (_jsx(IdentityRow, { label: "\u0412\u043E\u0437\u0440\u0430\u0441\u0442", children: _jsx("span", { className: "tabular-nums", children: age }) })) : null] })] }));
});
ContactCardIdentity.displayName = 'ContactCard.Identity';
export const ContactCard = Object.assign(ContactCardRoot, {
    Header: ContactCardHeader,
    Identities: ContactCardIdentities,
    Identity: ContactCardIdentity,
});
//# sourceMappingURL=contact-card.js.map